import { useState, useEffect, useRef } from "react";
import "./FolderPicker.css";
import { selectUser } from "../../redux/features/appData";
import { useSelector } from "react-redux";
import Modal from "../shared/Modal";
import {
  apiGetConfluenceFolderResources,
  apiGetConfluenceSpaces,
  apiGetGoogleDriveFolders,
  apiGetMSDriveFolderResources,
  apiGetMSDrives,
} from "../../services/DriveService";
import {
  CurrentlyAdding,
  DriveResource,
  GoogleDriveResource,
  MSDrive,
} from "winslow-core";
import { selectSupportedApps } from "../../redux/features/connectedApps/connectedAppsSlice";
import {
  acceptableResourceMimeType,
  makeUrlFromGoogleFile,
} from "winslow-core";
import { getAppLogoFromMimeType } from "winslow-core";

export const MULTI_SELECT_KEY = "Meta";

const isFolderMimeType = (mimeType: string): boolean => {
  if (mimeType === "application/vnd.google-apps.folder") {
    return true;
  }

  if (mimeType === "application/vnd.confluence.folder") {
    return true;
  }

  return false;
};

const isFolderKind = (kind: string): boolean => {
  if (kind === "space#page") {
    return true;
  }

  if (kind === "space#folder") {
    return true;
  }

  if (kind === "drive#folder") {
    return true;
  }

  return false;
};

type FolderPickerProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelectedFolder: (
    folder: { id: string; displayName: string; isFolder: boolean },
    includeSubfolders: boolean,
    msDriveId?: string
  ) => void;
  onSelectedFiles: (files: DriveResource[], msDriveId?: string) => void;
  msSiteName: string | null;
  msDrive: MSDrive | null;
  googleDrive: MSDrive | null;
  currentLocation?: FolderInfo;
  provider: CurrentlyAdding;
  handleOpenSharepointPicker: () => void;
};

export type FolderInfo = {
  id: string;
  isFolder: boolean;
  mimeType: string | undefined;
  displayName: string;
  url: string | undefined;
  parentId: string | null;
  parentDisplayName: string | null;
  subfolders?: FolderInfo[];
  files?: FolderInfo[];
};

export const FolderPicker = ({
  isOpen,
  onClose,
  onSelectedFolder,
  onSelectedFiles,
  msSiteName = null,
  msDrive,

  googleDrive,
  currentLocation,
  provider,

  handleOpenSharepointPicker,
}: FolderPickerProps) => {
  const storeUser = useSelector(selectUser);
  const supportedApps = useSelector(selectSupportedApps);

  const [rootFolder, setRootFolder] = useState<FolderInfo | null>(null);
  const [currentFolder, setCurrentFolder] = useState<FolderInfo | null>(null);
  const currentFolderRef = useRef<FolderInfo | null>(null);
  const [includeSubfolders, setIncludeSubfolders] = useState<boolean>(true);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [multiKeyDown, setMultiKeyDown] = useState<boolean>(false);

  const [msDriveId, setMSDriveId] = useState<string | undefined>(undefined);

  console.log("currentFolder: ", currentFolder);

  useEffect(() => {
    if (!rootFolder && storeUser) {
      // get the root folder info
      (async () => {
        let newRootFolder: FolderInfo | null = null;

        if (provider === "GOOGLE") {
          newRootFolder = await getGoogleRootFolder(googleDrive);
        } else if (provider === "ONEDRIVE") {
          newRootFolder = await getSharePointRootFolder(msDrive);
        } else if (provider === "SHAREPOINT") {
          newRootFolder = await getSharePointRootFolder(msDrive);
        }

        console.log("[FolderPicker/ue] newRootFolder: ", newRootFolder);

        if (newRootFolder) {
          setRootFolder({ ...newRootFolder });
          setCurrentFolder({ ...newRootFolder });
        }
      })();
    }

    window.addEventListener("keydown", handleGlobalKeyDown);
    window.addEventListener("keyup", handleGlobalKeyUp);

    return () => {
      window.removeEventListener("keydown", handleGlobalKeyDown);
      window.removeEventListener("keyup", handleGlobalKeyUp);
    };
  }, []);

  useEffect(() => {
    currentFolderRef.current = currentFolder;
  }, [currentFolder]);

  const getGoogleRootFolder = async (
    gDrive: MSDrive | null
  ): Promise<FolderInfo | null> => {
    if (!storeUser) {
      return null;
    }

    const driveData = await apiGetGoogleDriveFolders(
      storeUser.id,
      undefined,
      gDrive?.id
    );

    console.log("[getGoogleRootFolder] driveData: ", driveData);

    const newRootFolder: FolderInfo = {
      id: "",
      isFolder: true,
      mimeType: undefined,
      parentId: null,
      url: undefined,
      parentDisplayName: null,
      displayName: gDrive?.name || "My Drive",
      subfolders: undefined,
      files: undefined,
    };

    if (driveData && driveData.length > 0) {
      driveData.sort((a: GoogleDriveResource, b: GoogleDriveResource) => {
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      });

      const subfolders: FolderInfo[] = [];

      driveData.forEach((folder: GoogleDriveResource) => {
        if (isFolderMimeType(folder.mimeType)) {
          subfolders.push({
            id: folder.id,
            isFolder: true,
            mimeType: folder.mimeType,
            displayName: folder.name,
            url: undefined,
            parentId: "",
            parentDisplayName: gDrive?.name || "My Drive",
          });
        }

        return;
      });

      if (subfolders.length > 0) {
        newRootFolder.subfolders = subfolders;
      }

      const files: FolderInfo[] = [];

      driveData.forEach((folder: GoogleDriveResource) => {
        if (!isFolderMimeType(folder.mimeType)) {
          files.push({
            id: folder.id,
            isFolder: false,
            mimeType: folder.mimeType,
            displayName: folder.name,
            url: undefined,
            parentId: "",
            parentDisplayName: "My Drive",
          });
        }

        return;
      });

      if (files.length > 0) {
        newRootFolder.files = files;
      }
    }

    return newRootFolder;
  };

  const getSharePointRootFolder = async (
    msDrive: MSDrive | null = null
  ): Promise<FolderInfo | null> => {
    if (!storeUser) {
      return null;
    }

    let useDrive = msDrive;

    if (!useDrive) {
      const msDrives = await apiGetMSDrives(storeUser.id);

      console.log("[getSharePointRootFolder] msDrives: ", msDrives);

      if (!msDrives) {
        return null;
      }

      // grab only the drives not the sharepoints
      const msDrivesOnly = msDrives.filter((msDrive: MSDrive) => {
        return msDrive.type === "drive";
      });

      if (!msDrivesOnly || msDrivesOnly.length < 1) {
        return null;
      }

      useDrive = { ...msDrivesOnly[0] };
    }

    console.log("[getSharePointRootFolder] picking drive id: ", useDrive.id);
    setMSDriveId(useDrive.id);

    // set up the root folder with nothing but a name
    const newRootFolder: FolderInfo = {
      id: "",
      isFolder: true,
      mimeType: undefined,
      url: undefined,
      parentId: null,
      parentDisplayName: null,
      displayName: useDrive.name,
      subfolders: undefined,
      files: undefined,
    };

    const msDriveResources = await apiGetMSDriveFolderResources({
      userId: storeUser.id,
      driveId: useDrive.id,
      folderId: undefined,
    });

    console.log(
      "[getSharePointRootFolder] drive resources: ",
      msDriveResources
    );

    if (!msDriveResources) {
      return null;
    }

    if (msDriveResources && msDriveResources.length > 0) {
      msDriveResources.sort((a: DriveResource, b: DriveResource) => {
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      });

      const subfolders: FolderInfo[] = [];

      msDriveResources.forEach((resource: DriveResource) => {
        if (isFolderKind(resource.kind)) {
          subfolders.push({
            id: resource.id,
            isFolder: true,
            mimeType: resource.mimeType,
            displayName: resource.name,
            url: resource.url,
            parentId: "",
            parentDisplayName: useDrive.name,
          });
        }

        return;
      });

      if (subfolders.length > 0) {
        newRootFolder.subfolders = subfolders;
      }

      const files: FolderInfo[] = [];

      msDriveResources.forEach((resource: DriveResource) => {
        if (!isFolderKind(resource.kind)) {
          files.push({
            id: resource.id,
            isFolder: false,
            mimeType: resource.mimeType,
            displayName: resource.name,
            url: resource.url,
            parentId: "",
            parentDisplayName: useDrive.name,
          });
        }

        return;
      });

      if (files.length > 0) {
        newRootFolder.files = files;
      }
    }

    return newRootFolder;
  };

  const handleGlobalKeyDown = (e: any) => {
    console.log("GOT KEY: ", e.key);
    if (e.key === MULTI_SELECT_KEY) {
      setMultiKeyDown(true);
    }

    if (!multiKeyDown && (e.key === "a" || e.key === "A")) {
      selectAllFiles();
      e.stopPropagation();
      e.preventDefault();
    }
  };

  const handleGlobalKeyUp = (e: any) => {
    if (e.key === MULTI_SELECT_KEY) {
      setMultiKeyDown(false);
    }
  };

  const replaceSubfolder = (
    folderRoot: FolderInfo,
    replacementFolder: FolderInfo
  ) => {
    // 1. is it in our subfolders?
    // 2. no, recurse

    if (!folderRoot.subfolders) {
      return false;
    }

    const index = folderRoot.subfolders.findIndex((folder: FolderInfo) => {
      return folder.id === replacementFolder.id;
    });

    // we found it
    if (index !== -1) {
      folderRoot.subfolders[index] = replacementFolder;
      return true;
    }

    // we didn't find it so recurse
    let foundIt = false;

    for (let i = 0; i < folderRoot.subfolders.length; i++) {
      const result = replaceSubfolder(
        folderRoot.subfolders[i],
        replacementFolder
      );

      if (result) {
        foundIt = true;
        break;
      }
    }

    return foundIt;
  };

  const findFolderById = (
    folderRoot: FolderInfo,
    folderId: string
  ): FolderInfo | null => {
    // 1. is it in our subfolders?
    // 2. no, recurse

    if (folderRoot.id === folderId) {
      return folderRoot;
    }

    if (!folderRoot.subfolders) {
      return null;
    }

    const index = folderRoot.subfolders.findIndex((folder: FolderInfo) => {
      return folder.id === folderId;
    });

    // we found it
    if (index !== -1) {
      return folderRoot.subfolders[index];
    }

    // we didn't find it so recurse
    for (let i = 0; i < folderRoot.subfolders.length; i++) {
      const result = findFolderById(folderRoot.subfolders[i], folderId);

      if (result) {
        return result;
      }
    }

    return null;
  };

  const fileIsSelected = (id: string) => {
    return (
      selectedFiles.findIndex((fileId: string) => {
        return fileId === id;
      }) !== -1
    );
  };

  const selectFile = (id: string) => {
    const newSelectedFiles = [...selectedFiles];
    newSelectedFiles.push(id);
    setSelectedFiles(newSelectedFiles);
  };

  const unselectFile = (id: string) => {
    const index = selectedFiles.findIndex((fileId: string) => {
      return fileId === id;
    });

    if (index !== -1) {
      const newSelectedFiles = [
        ...selectedFiles.slice(0, index),
        ...selectedFiles.slice(index + 1),
      ];
      setSelectedFiles(newSelectedFiles);
    }
  };

  const resetSelectedFilesToOne = (fileId: string) => {
    setSelectedFiles([fileId]);
  };

  const selectAllFiles = () => {
    console.log("[FolderPicker/selectAllFiles] currentFolder: ", currentFolder);
    console.log(
      "[FolderPicker/selectAllFiles] currentFolder ref: ",
      currentFolderRef?.current
    );

    const theFolder = currentFolderRef?.current;

    let acceptableFiles: FolderInfo[] = [];

    if (theFolder?.files) {
      acceptableFiles = theFolder.files.filter((file: FolderInfo) => {
        return acceptableResourceMimeType(file.mimeType);
      });
    }

    setSelectedFiles(
      acceptableFiles.length > 0
        ? acceptableFiles.map((file: FolderInfo) => {
            return file.id;
          })
        : ([] as string[])
    );
  };

  const getFileIcon = (file: FolderInfo) => {
    let logo = getAppLogoFromMimeType(file.mimeType || "");
    let icon = logo
      ? "/icons/" + storeUser?.theme + "/app_logos/" + logo
      : "/icons/file.svg";
    return icon;
  };

  const loadSharePointDriveSubfolder = async (subfolder: FolderInfo) => {
    if (!storeUser || !msDriveId || !currentFolder) {
      return;
    }

    const msDriveResources = await apiGetMSDriveFolderResources({
      userId: storeUser.id,
      driveId: msDriveId,
      folderId: subfolder.id,
    });

    console.log(
      "[loadSharePointDriveSubfolder] drive resources: ",
      msDriveResources
    );
    console.log(
      "[loadSharePointDriveSubfolder] currentFolder: ",
      currentFolder
    );

    if (!msDriveResources) {
      return null;
    }

    const newSubfolder: FolderInfo = {
      id: subfolder.id,
      isFolder: true,
      mimeType: undefined,
      url: undefined,
      parentId: currentFolder.id,
      parentDisplayName: subfolder.parentDisplayName,
      displayName: subfolder.displayName,
      subfolders: undefined,
      files: undefined,
    };

    if (msDriveResources && msDriveResources.length > 0) {
      msDriveResources.sort((a: DriveResource, b: DriveResource) => {
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      });

      const subfolders: FolderInfo[] = [];

      msDriveResources.forEach((resource: DriveResource) => {
        if (isFolderKind(resource.kind)) {
          subfolders.push({
            id: resource.id,
            isFolder: true,
            mimeType: resource.mimeType,
            displayName: resource.name,
            url: resource.url,
            parentId: "",
            parentDisplayName: msDriveId,
          });
        }

        return;
      });

      if (subfolders.length > 0) {
        newSubfolder.subfolders = subfolders;
      }

      const files: FolderInfo[] = [];

      msDriveResources.forEach((resource: DriveResource) => {
        if (!isFolderKind(resource.kind)) {
          files.push({
            id: resource.id,
            isFolder: false,
            mimeType: resource.mimeType,
            displayName: resource.name,
            url: resource.url,
            parentId: "",
            parentDisplayName: msDriveId,
          });
        }

        return;
      });

      if (files.length > 0) {
        newSubfolder.files = files;
      }
    }

    if (rootFolder) {
      const newRootFolder = { ...rootFolder };
      replaceSubfolder(newRootFolder, newSubfolder);
      setRootFolder(newRootFolder);
    }

    setCurrentFolder({ ...newSubfolder });
  };

  const loadGoogleDriveSubfolder = async (subfolder: FolderInfo) => {
    if (!storeUser || !currentFolder) {
      return;
    }

    const driveData = await apiGetGoogleDriveFolders(
      storeUser.id,
      subfolder.id,
      googleDrive?.id
    );

    console.log(
      "[FolderPicker/loadGoogleDriveSubfolder] drive data: ",
      driveData
    );

    const newSubfolder: FolderInfo = {
      id: subfolder.id,
      isFolder: true,
      mimeType: undefined,
      url: undefined,
      parentId: currentFolder.id,
      parentDisplayName: subfolder.parentDisplayName,
      displayName: subfolder.displayName,
      subfolders: undefined,
      files: undefined,
    };

    if (driveData && driveData.length > 0) {
      driveData.sort((a: GoogleDriveResource, b: GoogleDriveResource) => {
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      });

      const subfolders: FolderInfo[] = [];

      driveData.forEach((folder: GoogleDriveResource) => {
        if (isFolderMimeType(folder.mimeType)) {
          subfolders.push({
            id: folder.id,
            isFolder: true,
            mimeType: folder.mimeType,
            url: undefined,
            displayName: folder.name,
            parentId: "",
            parentDisplayName: "My Drive",
          });
        }

        return;
      });

      if (subfolders.length > 0) {
        newSubfolder.subfolders = subfolders;
      }

      const files: FolderInfo[] = [];

      driveData.forEach((folder: GoogleDriveResource) => {
        if (!isFolderMimeType(folder.mimeType)) {
          files.push({
            id: folder.id,
            isFolder: false,
            mimeType: folder.mimeType,
            url: undefined,
            displayName: folder.name,
            parentId: "",
            parentDisplayName: "My Drive",
          });
        }

        return;
      });

      if (files.length > 0) {
        newSubfolder.files = files;
      }
    }

    // we need to replace the subfolder in the root tree with the new one that is
    // more flushed out and then we need to save it as the current folder

    if (rootFolder) {
      const newRootFolder = { ...rootFolder };
      replaceSubfolder(newRootFolder, newSubfolder);
      setRootFolder(newRootFolder);
    }

    setCurrentFolder({ ...newSubfolder });
  };

  const handleSelectFolder = () => {
    if (!currentFolder) {
      return;
    }

    onSelectedFolder(
      {
        id: currentFolder.id,
        displayName: currentFolder.displayName,
        isFolder: currentFolder.mimeType !== "application/vnd.confluence.page",
      },
      includeSubfolders,
      msDriveId
    );
  };

  const handleSelectedFiles = () => {
    if (!currentFolder) {
      return;
    }

    // console.log("[handleSelectedFiles] currentFolder: ", currentFolder);
    // console.log("[handleSelectedFiles] msDriveId: ", msDriveId);

    const resources: DriveResource[] = [];

    selectedFiles.forEach((fileId: string) => {
      if (currentFolder && currentFolder.files) {
        const index = currentFolder.files.findIndex((file: FolderInfo) => {
          return file.id === fileId;
        });

        if (index !== -1) {
          resources.push({
            url: msDriveId
              ? currentFolder.files[index].url || ""
              : makeUrlFromGoogleFile(
                  currentFolder.files[index].id,
                  currentFolder.files[index].mimeType || ""
                ),
            folderSlug: currentFolder.id,
            folderName: currentFolder.displayName,
            completePath: currentFolder.displayName,
            folderRootId: currentFolder.id,
            id: currentFolder.files[index].id,
            kind: "drive#file",
            mimeType: currentFolder.files[index].mimeType || "",
            name: currentFolder.files[index].displayName,
          });
        }
      }
    });

    console.log("[handleSelectedFiles] selected resources: ", resources);

    onSelectedFiles(resources, msDriveId);
  };

  const handleSelectParentFolder = async () => {
    if (!currentFolder || !storeUser) {
      return;
    }

    // console.log("[HandleSelectParent] rootFolder: ", rootFolder);
    // console.log("[HandleSelectParent] currentFolder: ", currentFolder);

    if (
      rootFolder &&
      (currentFolder.parentId === "" || currentFolder.parentId)
    ) {
      const parentFolder = findFolderById(rootFolder, currentFolder.parentId);

      if (parentFolder) {
        // Load fresh data for the parent folder based on provider
        if (provider === CurrentlyAdding.GOOGLE) {
          await loadGoogleDriveSubfolder(parentFolder);
        } else if (provider === CurrentlyAdding.SHAREPOINT) {
          await loadSharePointDriveSubfolder(parentFolder);
        } else {
          setCurrentFolder({ ...parentFolder });
        }
      }
    }
  };

  const handleSelectSubfolder = async (subfolder: FolderInfo) => {
    if (provider === CurrentlyAdding.GOOGLE) {
      await loadGoogleDriveSubfolder(subfolder);
    } else if (provider === CurrentlyAdding.SHAREPOINT) {
      await loadSharePointDriveSubfolder(subfolder);
    }

    setSelectedFiles([]);
  };

  return (
    <Modal
      isOpen={isOpen}
      style={null}
      onClose={onClose}
      canClose={false}
      allowClickout={true}>
      <div className="folder-picker-container">
        <div className="folder-picker-header">
          <div className="folder-picker-title">Pick a Folder</div>
          <div className="folder-picker-buttons">
            <img className="icon-close" onClick={onClose} alt="" />
          </div>
        </div>

        {currentLocation && (
          <div className="folder-picker-header">
            <div className="folder-picker-current">Current location:</div>
            <div className="folder-picker-current-card">
              <div
                className="folder-picker-current-icon"
                onClick={() => handleSelectParentFolder()}>
                <img src="/icons/folder.svg" alt="" />
              </div>
              <div className="folder-picker-current-name">
                {currentLocation?.displayName}
              </div>
            </div>
          </div>
        )}
        {currentFolder && (
          <div className="folder-picker-back">
            <div className="folder-picker-back-container">
              {currentFolder.id === "" &&
                provider === CurrentlyAdding.SHAREPOINT && (
                  <div
                    className="folder-picker-arrow"
                    onClick={() => {
                      handleOpenSharepointPicker();
                    }}>
                    <img src="/icons/arrow_left.png" alt="" />
                  </div>
                )}

              {currentFolder.id !== "" && (
                <div
                  className="folder-picker-arrow"
                  onClick={() => handleSelectParentFolder()}>
                  <img src="/icons/arrow_left.png" alt="" />
                </div>
              )}
              <div className="folder-picker-subfolder-name">
                {msSiteName ? msSiteName + "> " : ""}
                {currentFolder.displayName}
              </div>
              <div className="folder-picker-select-folder">
                {selectedFiles.length === 0 && (
                  <>
                    <div
                      className="folder-picker-select-include"
                      onClick={() => setIncludeSubfolders(!includeSubfolders)}>
                      <img
                        src={
                          includeSubfolders ? "/check_on.svg" : "/check_off.svg"
                        }
                        alt=""
                      />
                      &nbsp;include subfolders&nbsp;&nbsp;
                    </div>

                    <button
                      id="folder-picker-select-button"
                      onClick={handleSelectFolder}>
                      Select
                    </button>
                  </>
                )}
                {selectedFiles.length > 0 && (
                  <div className="folder-picker-select-include">
                    <button
                      id="folder-picker-select-button"
                      onClick={handleSelectedFiles}>
                      Select {selectedFiles.length} Files
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="folder-picker-divider"></div>

        {(currentFolder?.subfolders || currentFolder?.files) && (
          <div className="folder-picker-contents">
            {currentFolder?.subfolders &&
              currentFolder.subfolders.map((subfolder: FolderInfo) => {
                return (
                  <div
                    className="folder-picker-entry"
                    onClick={() => handleSelectSubfolder(subfolder)}>
                    <div className="folder-picker-icon">
                      {subfolder.mimeType ===
                      "application/vnd.confluence.page" ? (
                        <img src="/icons/confluence.svg" alt="" />
                      ) : (
                        <img src="/icons/folder.svg" alt="" />
                      )}
                    </div>
                    <div className="folder-picker-name">
                      {subfolder.displayName}
                    </div>
                  </div>
                );
              })}

            {currentFolder?.files &&
              currentFolder.files.map((file: FolderInfo) => {
                return (
                  <div
                    className={
                      fileIsSelected(file.id)
                        ? "folder-picker-entry-selected"
                        : acceptableResourceMimeType(file.mimeType)
                          ? "folder-picker-entry"
                          : "folder-picker-entry-unpickable"
                    }
                    onClick={() => {
                      if (!acceptableResourceMimeType(file.mimeType)) {
                        return;
                      }

                      if (!multiKeyDown) {
                        resetSelectedFilesToOne(file.id);
                        return;
                      }

                      if (fileIsSelected(file.id)) {
                        unselectFile(file.id);
                      } else {
                        selectFile(file.id);
                      }
                    }}>
                    <div className="folder-picker-icon">
                      <img src={getFileIcon(file)} alt="" />
                    </div>
                    <div className="folder-picker-name">{file.displayName}</div>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </Modal>
  );
};
